import { apiFetch, apiUploadFormData } from "@/lib/api";

export type CupsPrinterListItem = {
  name: string;
  description?: string;
  location?: string;
  status?: string;
  type?: string;
};

export type CupsPrinterListResponse = {
  printers: CupsPrinterListItem[];
};

export type CupsPrintResponse = {
  success: true;
  printer: string;
  copies: number;
};

export const MISC_CUPS_PRINTERS_QUERY_KEY = ["personal", "cups-printers"] as const;

export async function fetchMiscCupsPrinters(): Promise<CupsPrinterListResponse> {
  return apiFetch<CupsPrinterListResponse>("/api/personal/printing/printers");
}

export async function postMiscCupsPrint(formData: FormData): Promise<CupsPrintResponse> {
  return apiUploadFormData<CupsPrintResponse>("/api/personal/printing/print", formData);
}

export function formatCupsPrinterOptionLabel(printer: CupsPrinterListItem): string {
  let label = printer.description?.trim() || printer.name;
  if (printer.location?.trim()) {
    label += ` (${printer.location.trim()})`;
  }
  return label;
}

export const MISC_PRINT_LAST_PRINTER_STORAGE_KEY = "personal-print-last-cups-printer";

/** Outbound packing slip modal remembers its own default letter printer. */
export const SHIPPING_OUTBOUND_CUPS_PRINTER_STORAGE_KEY = "shipping-outbound-cups-printer";

/** Quality case traveler print remembers the Case Management letter printer. */
export const QUALITY_CASE_CUPS_PRINTER_STORAGE_KEY = "quality-case-cups-printer";

export function isCaseManagementCupsPrinter(printer: CupsPrinterListItem): boolean {
  return /case[\s_-]*management/i.test(`${printer.name} ${printer.description ?? ""} ${printer.location ?? ""}`);
}

export function pickQualityCaseCupsPrinter(printers: CupsPrinterListItem[], saved = ""): string {
  const trimmedSaved = saved.trim();
  if (trimmedSaved !== "" && printers.some((row) => row.name === trimmedSaved)) {
    return trimmedSaved;
  }
  const named = printers.find((row) => isCaseManagementCupsPrinter(row));
  if (named != null) return named.name;
  const office = printers.find((row) => /office/i.test(`${row.location ?? ""} ${row.description ?? ""}`));
  return office?.name ?? printers[0]?.name ?? "Case_Management";
}

export function pdfBase64ToPrintFile(base64: string, filename: string): File {
  const trimmed = base64.trim();
  const payload = trimmed.startsWith("data:application/pdf") ? trimmed.split(",")[1] ?? "" : trimmed;
  const bytes = atob(payload);
  const arr = new Uint8Array(bytes.length);
  for (let index = 0; index < bytes.length; index += 1) {
    arr[index] = bytes.charCodeAt(index);
  }
  return new File([arr], filename, { type: "application/pdf" });
}

export function downloadPdfBase64(base64: string, filename: string): void {
  const file = pdfBase64ToPrintFile(base64, filename);
  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Prefer office letter printers for document packing slips; fall back to the full list. */
export function cupsPrintersForDocumentPrint(printers: CupsPrinterListItem[]): CupsPrinterListItem[] {
  const letterPrinters = printers.filter((printer) => printer.type === "letter");
  return letterPrinters.length > 0 ? letterPrinters : printers;
}

export type ZplPrintResponse = {
  success: true;
  printer: string;
  copies: number;
};

export const MISC_PRINT_MODE_STORAGE_KEY = "personal-print-mode";

export type MiscPrintMode = "document" | "label";

export async function postMiscZplPrint(body: {
  zpl: string;
  printer: string;
  copies?: number;
}): Promise<ZplPrintResponse> {
  return apiFetch<ZplPrintResponse>("/api/personal/printing/zpl-print", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export const MISC_ZPL_PRINT_LAST_PRINTER_STORAGE_KEY = "personal-print-last-zpl-printer";

export const PDD_ZPL_CATCH_QUERY_KEY = ["personal", "pdd-zpl-catch"] as const;

export type PddZplCatchKind = "unit" | "carton" | "other";

export type PddZplCatchJob = {
  id: string;
  filename: string;
  originalName: string;
  receivedAt: string;
  format: string;
  kind: PddZplCatchKind;
  printCopies: number;
  labelQty: number | null;
  catalog: string | null;
  sku: string | null;
  bytes: number;
};

export type PddZplCatchListResponse = {
  catchDir: string;
  available: boolean;
  jobs: PddZplCatchJob[];
};

export type PddZplCatchPrintResponse = {
  success: true;
  printer: string;
  printed: { id: string; kind: PddZplCatchKind }[];
};

export type PddZplCatchPreviewResponse = {
  id: string;
  kind: PddZplCatchKind;
  engine: "hub" | "labelary";
  pngBase64: string;
};

export async function fetchPddZplCatch(): Promise<PddZplCatchListResponse> {
  return apiFetch<PddZplCatchListResponse>("/api/personal/printing/pdd-catch");
}

export async function fetchPddZplCatchPreview(id: string): Promise<PddZplCatchPreviewResponse> {
  const params = new URLSearchParams({ id });
  return apiFetch<PddZplCatchPreviewResponse>(`/api/personal/printing/pdd-catch/preview?${params.toString()}`);
}

export async function postPddZplCatchPrint(body: {
  ids: string[];
  printer: string;
}): Promise<PddZplCatchPrintResponse> {
  return apiFetch<PddZplCatchPrintResponse>("/api/personal/printing/pdd-catch/print", {
    method: "POST",
    body,
  });
}

export function pddZplCatchKindLabel(kind: PddZplCatchKind): string {
  if (kind === "unit") return "Unit";
  if (kind === "carton") return "Carton";
  return "Other";
}

export function pddZplCatchPrintSortKey(kind: PddZplCatchKind): number {
  if (kind === "unit") return 0;
  if (kind === "carton") return 1;
  return 2;
}

export const MISC_PRINT_ALLOWED_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".txt",
  ".ps",
  ".eps",
  ".zpl",
] as const;

export function miscPrintModeForFile(file: File | null): MiscPrintMode {
  if (file == null) return "document";
  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase() : "";
  return ext === ".zpl" ? "label" : "document";
}

export function miscPrintFileAllowed(file: File, mode: MiscPrintMode): boolean {
  const ext = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase() : "";
  if (mode === "label") return ext === ".zpl";
  if (ext === ".zpl") return false;
  if ((MISC_PRINT_ALLOWED_EXTENSIONS as readonly string[]).includes(ext) && ext !== ".zpl") return true;
  return (
    file.type === "application/pdf" ||
    file.type.startsWith("image/") ||
    file.type === "text/plain" ||
    file.type === "application/postscript"
  );
}
